import type { Observation } from "@medplum/fhirtypes";

import { Systems } from "@/fhir/enums/systems";
import { joinTokenString } from "@/utils/medplum";

import {
  ClientPostpartumFlowsheetForms,
  NewbornPostpartumFlowsheetForms,
} from "./postpartum-flowsheets";

/**
 * QH observation codes charted on the related (newborn) patient for the labor encounter
 * postpartum flowsheets (not only cord / First Latch).
 */
const RELATED_PATIENT_LABOR_POSTPARTUM_FLOWSHEET_CODE_SET = new Set<string>([
  ...Object.values(NewbornPostpartumFlowsheetForms),
  ClientPostpartumFlowsheetForms.ClientCordCutClamped,
  ClientPostpartumFlowsheetForms.ClientFirstLatch,
]);

/** Exported list of related-patient postpartum flowsheet codes (deduped). */
export const SHARED_POSTPARTUM_RELATED_CODES: readonly string[] = Array.from(
  RELATED_PATIENT_LABOR_POSTPARTUM_FLOWSHEET_CODE_SET,
);

/**
 * Comma-joined `system|code` tokens for a narrow Medplum `code` search when needed.
 * Prefer encounter + related subject search with client-side filter (avoids URL length limits).
 */
export const RELATED_PATIENT_POSTPARTUM_CODE_SEARCH = SHARED_POSTPARTUM_RELATED_CODES.map((code) =>
  joinTokenString(Systems.QH, code),
).join(",");

/** True when any `code.coding` entry matches a related-patient labor postpartum flowsheet code. */
export function observationHasSharedPostpartumRelatedCode(observation: Observation): boolean {
  const codes =
    observation.code?.coding
      ?.map((coding) => coding.code)
      .filter((code): code is string => Boolean(code)) ?? [];
  return codes.some((code) => RELATED_PATIENT_LABOR_POSTPARTUM_FLOWSHEET_CODE_SET.has(code));
}
